import * as R from 'ramda';

import { Menu } from '@atom/mui';
import { WorkOrderTask } from '@atom/types/work';
import { setDisplayDate } from '@atom/utilities/timeUtilities';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import TextOverflowTooltip from '../tooltip/TextOverflowTooltip';

import classes from './workTasks.module.css';

type WorkTasksProps = {
  tasks?: WorkOrderTask[];
  width?: string;
  showDateTime?: boolean;
};

const getTaskDateSubtext = (
  task: WorkOrderTask,
  showDateTime: boolean,
): string => {
  const dateFormat = showDateTime ? 'MM/dd/yyyy hh:mm a' : 'MM/dd/yyyy';

  if (task.completedDate) {
    return `Completed on ${setDisplayDate(task.completedDate, dateFormat)}`;
  }

  return task.dueDate
    ? `Due on ${setDisplayDate(task.dueDate, dateFormat)}`
    : '';
};

const WorkTasks = ({
  tasks = [],
  width = '14rem',
  showDateTime = false,
}: WorkTasksProps) => {
  if (isNilOrEmpty(tasks)) {
    return <div />;
  }

  const firstTask = tasks[0];
  const additionalTasks = R.drop(1, tasks);

  const renderTaskDate = (task: WorkOrderTask) => {
    const subtext = getTaskDateSubtext(task, showDateTime);

    return subtext ? (
      <div className={classes.taskDueDate}>{subtext}</div>
    ) : null;
  };

  const additionalTasksMenu = (
    <Menu
      noIconButton
      variant="menu"
      disableAutoFocusItem
      maxWidth="30rem"
      keepMounted={false}
      dataCyLabel="workTasksAdditionalTasksButton"
      icon={
        <div className={classes.additionalTasksCount}>
          {`+${additionalTasks.length}`}
        </div>
      }
    >
      {additionalTasks.map(task => (
        <div
          key={task.id}
          className={classes.menuTask}
          data-cy={`workTasksAdditionalTask-${task.name}`}
        >
          <div>{task.name}</div>
          {renderTaskDate(task)}
        </div>
      ))}
    </Menu>
  );

  return (
    <div>
      {tasks?.length === 1 ? (
        <TextOverflowTooltip text={firstTask.name} width={width} lightTooltip />
      ) : (
        <div className={classes.overflowContainer}>
          <TextOverflowTooltip
            text={firstTask.name}
            width={width}
            lightTooltip
          />
          {additionalTasksMenu}
        </div>
      )}
      {renderTaskDate(firstTask)}
    </div>
  );
};

export default WorkTasks;
